import { ref } from "vue";
import { useSetting } from "@/Hook/useSetting";
import generateShadeScale from "@/utils/shade";

/*
 | Design tokens (ported from www's useTheme). Maps the CMS `site.settings`
 | array into reactive `colors` + `typography` objects, and `defineTheme()`
 | expands the brand colours into the 11-step scales PrimeVue's Aura preset
 | expects. Defaults are tuned to ta2's portal look (slate canvas, teal brand,
 | dark topbar) so the UI is stable before any settings are authored.
 |
 | `props` may be passed at app-boot (init.ts) when usePage() isn't available;
 | inside components it falls back to the active page props.
 */
export default function useTheme(props?: any) {
    const { getSetting } = useSetting(props);

    const colors = ref({
       body: {
            background: getSetting("body_background", "#ffffff"),
            text_color: getSetting("body_text_color", "#000000"),
        },

        primary: getSetting("primary_color", "#50C878"),

        primary_button_color: getSetting("primary_button_color", "#ffffff"),

        secondary: getSetting("secondary_color", "#0e0e0e"),

        footer: {
            background: getSetting("footer_background", "#ffffff"),
            text_color: getSetting("footer_text_color", "#000000"),
        },

        topbar: {
            background: getSetting("topbar_background", "#ffffff"),
            text_color: getSetting("topbar_text_color", "#000000"),
            accent: getSetting("topbar_ribbon_background", "#000000"),
        },
    });

    const typography = ref({
        body: {
            font_family: getSetting("body_font_family", "Roboto"),
            desktop_size: getSetting("body_size_desktop"),
            mobile_size: getSetting("body_size_mobile"),
        },
        h1: {
            font_family: getSetting("h1_font_family"),
            desktop_size: getSetting("h1_size_desktop"),
            mobile_size: getSetting("h1_size_mobile"),
        },
        h2: {
            font_family: getSetting("h2_font_family"),
            desktop_size: getSetting("h2_size_desktop"),
            mobile_size: getSetting("h2_size_mobile"),
        },
        h3: {
            font_family: getSetting("h3_font_family"),
            desktop_size: getSetting("h3_size_desktop"),
            mobile_size: getSetting("h3_size_mobile"),
        },
        h4: {
            font_family: getSetting("h4_font_family"),
            desktop_size: getSetting("h4_size_desktop"),
            mobile_size: getSetting("h4_size_mobile"),
        },
        h5: {
            font_family: getSetting("h5_font_family"),
            desktop_size: getSetting("h5_size_desktop"),
            mobile_size: getSetting("h5_size_mobile"),
        },
        h6: {
            font_family: getSetting("h6_font_family"),
            desktop_size: getSetting("h6_size_desktop"),
            mobile_size: getSetting("h6_size_mobile"),
        },
    });

    const defineTheme = function () {
        const shades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

        const primary_shades = generateShadeScale(colors.value.primary, 11, 6);
        const secondary_shades = generateShadeScale(
            colors.value.secondary,
            11,
            6,
        );

        const primary = Object.fromEntries(
            primary_shades.map((shade, index) => [shades[index], shade]),
        );

        const secondary = Object.fromEntries(
            secondary_shades.map((shade, index) => [shades[index], shade]),
        );

        return {
            semantic: {
                primary,
                secondary,
            },
            // Component overrides mirrored from www's useTheme so shared chrome
            // (topbar menubar, datepicker, tooltips) renders identically.
            components: {
                menubar: {
                    gap: "20px",
                    background: "transparent",
                    border: 'none',
                    item: {
                        focus: {
                            background: "transparent",
                        },
                        active: {
                            background: "transparent",
                        },
                    },
                },

                datepicker: {
                    panel: {
                        border: {
                            color: "transparent",
                        },
                    },
                    header: {
                        border: {
                            color: "transparent",
                        },
                    },
                },

                // Modern, high-contrast black tooltip. Replaces Aura's default
                // bluish surface and constrains the width so it never overflows
                // on mobile.
                tooltip: {
                    root: {
                        background: "#000000",
                        color: "#ffffff",
                        maxWidth: "min(20rem, calc(100vw - 1rem))",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "0.625rem",
                        gutter: "0.375rem",
                        shadow: "0 10px 30px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.18)",
                    },
                },
            },
        };
    };

    return { colors, typography, defineTheme };
}
